import { IPublicClientApplication, SilentRequest, AuthenticationResult, AuthError } from '@azure/msal-browser';
import { AZURE_AD_CONFIG } from '../config/azureAdConfig';

interface RetryConfig {
  maxRetries: number;
  initialDelay: number;
  maxDelay: number;
  backoffMultiplier: number;
}

const DEFAULT_RETRY_CONFIG: RetryConfig = {
  maxRetries: 4, // Increased from 3 for better resilience
  initialDelay: 500,
  maxDelay: 8000, // Increased from 5000
  backoffMultiplier: 2
};

/**
 * Acquires token with exponential backoff retry logic
 * This helps handle transient network failures that can cause authentication to fail
 * across all browsers and sessions
 */
export const acquireTokenSilentWithRetry = async (
  instance: IPublicClientApplication,
  request: SilentRequest,
  config: Partial<RetryConfig> = {}
): Promise<AuthenticationResult> => {
  const retryConfig = { ...DEFAULT_RETRY_CONFIG, ...config };
  let lastError: Error | null = null;
  
  for (let attempt = 0; attempt <= retryConfig.maxRetries; attempt++) {
    try {
      if (AZURE_AD_CONFIG.ENABLE_AUTH_LOGGING && attempt > 0) {
        console.log(`🔄 Token acquisition attempt ${attempt + 1}/${retryConfig.maxRetries + 1}`);
      }
      
      const response = await instance.acquireTokenSilent(request);
      
      if (attempt > 0) {
        console.log(`✅ Token acquired successfully on attempt ${attempt + 1}`);
      }
      
      return response;
      
    } catch (error) {
      const authError = error as AuthError;
      lastError = authError;
      
      // Log the error details for debugging
      console.error(`❌ Token acquisition attempt ${attempt + 1} failed:`, {
        errorCode: authError.errorCode,
        errorMessage: authError.message,
        subError: authError.subError,
        correlationId: (authError as any).correlationId
      });
      
      // Don't retry for these errors - they require user interaction
      const nonRetryableErrors = [
        'consent_required',
        'interaction_required',
        'login_required',
        'user_cancelled',
        'invalid_grant'
      ];
      
      if (authError.errorCode && nonRetryableErrors.includes(authError.errorCode)) {
        console.warn('⚠️ Non-retryable error encountered:', authError.errorCode);
        throw error;
      }
      
      // Check if this is a transient error worth retrying
      const isTransientError = 
        // Network errors
        authError.message?.toLowerCase().includes('network') ||
        authError.message?.toLowerCase().includes('timeout') ||
        authError.message?.toLowerCase().includes('failed to fetch') ||
        authError.message?.toLowerCase().includes('fetch') ||
        authError.errorCode?.includes('network') ||
        // Iframe-specific errors (your case)
        authError.errorCode === 'monitor_window_timeout' ||
        authError.errorCode === 'iframe_timeout' ||
        authError.message?.includes('iframe') ||
        // Other transient errors
        authError.errorCode === 'endpoints_resolution_error' ||
        authError.errorCode === 'empty_response' ||
        // Sometimes network errors don't have codes
        !authError.errorCode;
      
      if (!isTransientError || attempt === retryConfig.maxRetries) {
        console.error(`❌ Token acquisition failed after ${attempt + 1} attempts`);
        throw error;
      }
      
      // Calculate delay with exponential backoff
      const delay = Math.min(
        retryConfig.initialDelay * Math.pow(retryConfig.backoffMultiplier, attempt),
        retryConfig.maxDelay
      );
      
      console.warn(`⏳ Retrying token acquisition in ${delay}ms (attempt ${attempt + 1}/${retryConfig.maxRetries})`);
      
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  
  console.error('❌ All token acquisition attempts exhausted');
  throw lastError || new Error('Token acquisition failed after all retries');
};

/**
 * Utility to check if an error is retryable
 */
export const isRetryableError = (error: any): boolean => {
  const authError = error as AuthError;
  
  const nonRetryableErrors = [
    'consent_required',
    'interaction_required',
    'login_required',
    'user_cancelled',
    'invalid_grant'
  ];
  
  if (authError.errorCode && nonRetryableErrors.includes(authError.errorCode)) {
    return false;
  }
  
  // Network errors are retryable
  const isNetworkError = authError.message?.toLowerCase().includes('network') ||
                        authError.message?.toLowerCase().includes('timeout') ||
                        authError.message?.toLowerCase().includes('failed to fetch');
  
  return isNetworkError;
};
